import classNames from 'classnames'
import * as React from 'react'
import {useEffect, useRef, memo} from 'react'
import {useDrag, useDrop} from 'react-dnd'
import {getEmptyImage} from 'react-dnd-html5-backend'

import BuildDetails from '../../../common/Builds/BuildDetails/BuildDetails'
import type {Props as BuildDetailsProps} from '../../../common/Builds/BuildDetails/BuildDetails.types'
import IconButton from '../../../common/IconButton/IconButton'
import {firstBuildOrder} from '../QueueBuildList/useQueueBuildDragDrop'

import styles from './QueueBuild.css'

export const BuildDragType = 'BuildDragType'

type Props = BuildDetailsProps & {
  prevBuildId: number | undefined
  nextBuildId: number | undefined
  isQueued: boolean
  hoveredDropBuildId: number | undefined
  droppedBuildId: number | undefined
  setHoveredDropBuildId: (buildId: number | undefined) => void
  onDrop: (item: QueueBuildDragItem) => void
  isDraggable: boolean | undefined
}

export type QueueBuildDragItem = {
  buildId: number
  width: number
  height: number
  x: number
  y: number
}

const emptyRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
}

function QueueBuild(props: Props) {
  const {
    buildId,
    isDraggable,
    prevBuildId = firstBuildOrder,
    nextBuildId,
    isQueued,
    hoveredDropBuildId,
    setHoveredDropBuildId,
    droppedBuildId,
    onDrop,
  } = props

  const rowRef = useRef<HTMLDivElement | null>(null)
  const dropContentRef = useRef<HTMLDivElement | null>(null)

  const [{dragBuildId}, dropRef] = useDrop(
    () => ({
      accept: BuildDragType,
      collect: monitor => ({
        dragBuildId: monitor.getItem<QueueBuildDragItem>()?.buildId,
      }),
      hover: (_item, monitor) => {
        const rect = dropContentRef.current?.getBoundingClientRect()
        const clientOffset = monitor.getClientOffset()

        if (rect && clientOffset) {
          const relativeY = clientOffset.y - rect.y

          setHoveredDropBuildId(relativeY - rect.height / 2 >= 0 ? buildId : prevBuildId)
        }
      },
      drop: onDrop,
    }),
    [buildId, prevBuildId, onDrop, dropContentRef],
  )

  const [, dragRef, dragPreview] = useDrag(
    () => ({
      type: BuildDragType,
      item: () => {
        const rect = rowRef.current?.getBoundingClientRect() ?? emptyRect

        return {
          x: rect.x,
          y: rect.y,
          height: rect.height,
          width: rect.width,
          buildId,
        }
      },
      end: () => {
        setHoveredDropBuildId(undefined)
      },
    }),
    [buildId, rowRef],
  )

  useEffect(() => {
    dragPreview(getEmptyImage(), {captureDraggingState: false})
  }, [dragPreview])

  const isDragging = dragBuildId !== undefined
  const isDraggingBuild = isDragging && buildId === dragBuildId
  const canDropAbove = prevBuildId === hoveredDropBuildId && prevBuildId !== dragBuildId
  const canDropBelow = buildId === hoveredDropBuildId && nextBuildId !== dragBuildId
  const isDroppedBuild = buildId === droppedBuildId

  return (
    <BuildDetails containerClassName={styles.hoverableWrapper} {...props}>
      <>
        {isDraggable && isQueued && (
          <div ref={dragRef} className={classNames(styles.handle, styles.hideableCell)}>
            <IconButton
              className={styles.handleIcon}
              title={'Use drag-and-drop to reorder the queue'}
              icon={'drag'}
            />
          </div>
        )}

        {!isDraggingBuild && canDropAbove && (
          <div className={classNames(styles.separator, styles.above)} />
        )}

        {!isDraggingBuild && canDropBelow && (
          <div className={classNames(styles.separator, styles.below)} />
        )}

        <div
          ref={rowRef}
          className={classNames(styles.fakeContent, {
            [styles.invisible]: !isDroppedBuild,
            [styles.overlay]: isDroppedBuild,
            [styles.blink]: isDroppedBuild,
          })}
        />

        <div
          ref={ref => {
            dropRef(ref)
            dropContentRef.current = ref
          }}
          className={classNames(styles.drop, {
            [styles.blur]: isDraggingBuild,
            [styles.invisible]: !isDragging,
            [styles.overlay]: isDragging,
          })}
        />
      </>
    </BuildDetails>
  )
}

export default memo(QueueBuild)
